import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Upload, Loader, Camera, X, Scan, FileText, TrendingDown, Sparkles } from 'lucide-react';
import { useDialog } from '../context/DialogContext';
import { useCapture } from '../context/CaptureContext';
import useIsMobile from '../hooks/useIsMobile';
import useExpenseForm from '../hooks/useExpenseForm';
import PreviewModal from '../components/PreviewModal';
import AutocompleteInput from '../components/AutocompleteInput';
import MobileExpenseForm from './MobileExpenseForm';
import { CURRENCIES } from '../utils/currencies';
import { getPreviewUrl } from '../utils/previewUrl';
import { EXPENSE_CATEGORIES } from '../utils/categories';

function ExpenseForm() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileExpenseForm /> : <DesktopExpenseForm />;
}

function DesktopExpenseForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useDialog();
  const { openScanner } = useCapture();
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const {
    isEditing, loading, loadError, saving, file, preview, fileType, existingImageUrl,
    ocrLoading, ocrProgress, ocrSuggestions, setOcrSuggestions, applySuggestions,
    formData, handleChange, suggestions, trips, convertedAmount, conversionRate,
    processFile, clearFile, submit,
  } = useExpenseForm({ id });

  useEffect(() => {
    if (loadError) {
      toast('Error loading expense', 'error');
      navigate('/expenses');
    }
  }, [loadError, navigate, toast]);

  const onDropFiles = useCallback(async (acceptedFiles) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;
    await processFile(selectedFile);
  }, [processFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropFiles,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'application/pdf': ['.pdf'],
    },
    multiple: false,
  });

  const startScan = () => openScanner((capturedFile, previewUrl) => processFile(capturedFile, previewUrl));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await submit();
    if (result.status === 'saved') {
      navigate('/expenses');
    } else if (result.status === 'queued') {
      toast('Saved offline — it will upload when you are back online', 'info');
      navigate('/expenses');
    } else {
      toast(result.message, 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Edit Expense' : 'New Expense'}
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          {isEditing 
            ? 'Update the expense details below'
            : 'Take a photo or upload a PDF and fill in the details'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* File drop zone */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Receipt photo or PDF</h2>
          
          {!preview ? (
            <div className="space-y-4">
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">
                  {isDragActive
                    ? 'Drop the file here...'
                    : 'Drag and drop an image or PDF, or click to select'}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  JPG, PNG, GIF, WEBP or PDF up to 50MB
                </p>
              </div>

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={startScan}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                >
                  <Scan className="w-5 h-5" />
                  Scan a document
                </button>
              </div>

            </div>
          ) : (
            <div className="space-y-4">
              {existingImageUrl && !file && (
                <p className="text-sm text-gray-500 text-center">
                  Current receipt
                </p>
              )}
              <div className="relative">
                {fileType === 'pdf' ? (
                  <div 
                    className="w-full h-64 bg-gray-100 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => setShowPreviewModal(true)}
                  >
                    <FileText className="w-16 h-16 text-red-500 mb-2" />
                    <p className="text-sm text-gray-700 font-medium">PDF file</p>
                    <p className="text-xs text-gray-500 mt-1">Click to preview</p>
                  </div>
                ) : (
                  <img
                    src={getPreviewUrl(preview)}
                    alt="Preview"
                    className="w-full h-64 object-contain rounded-lg bg-gray-100 cursor-pointer"
                    onClick={() => setShowPreviewModal(true)}
                  />
                )}
                {/* Only show X button for new uploads, not for existing images when editing */}
                {(!isEditing || file) && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowPreviewModal(false);
                      clearFile();
                    }}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              
              {/* Replace image button for edit mode */}
              {isEditing && existingImageUrl && !file && (
                <div className="flex gap-3 justify-center">
                  <div {...getRootProps()} className="cursor-pointer">
                    <input {...getInputProps()} />
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      Upload new
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={startScan}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                  >
                    <Scan className="w-4 h-4" />
                    Scan new
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* OCR status + suggestions */}
        {ocrLoading && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
            <Loader className="w-4 h-4 animate-spin text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">Reading receipt…</p>
              <div className="mt-1 h-1.5 bg-amber-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500 rounded-full transition-all duration-200"
                  style={{ width: `${ocrProgress}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {ocrSuggestions && !ocrLoading && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-600" />
                <p className="text-sm font-semibold text-amber-800">Suggestions from receipt scan</p>
              </div>
              <button
                type="button"
                onClick={() => setOcrSuggestions(null)}
                className="text-amber-500 hover:text-amber-700"
                aria-label="Dismiss suggestions"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {ocrSuggestions.amount && (
                <button
                  type="button"
                  onClick={() => applySuggestions({ amount: ocrSuggestions.amount })}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-amber-300 rounded-full text-sm text-amber-800 hover:bg-amber-100 transition-colors"
                >
                  Amount: <strong>{ocrSuggestions.amount} €</strong>
                </button>
              )}
              {ocrSuggestions.date && (
                <button
                  type="button"
                  onClick={() => applySuggestions({ date: ocrSuggestions.date })}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-amber-300 rounded-full text-sm text-amber-800 hover:bg-amber-100 transition-colors"
                >
                  Date: <strong>{ocrSuggestions.date}</strong>
                </button>
              )}
              {ocrSuggestions.merchant && (
                <button
                  type="button"
                  onClick={() => applySuggestions({ merchant: ocrSuggestions.merchant })}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-amber-300 rounded-full text-sm text-amber-800 hover:bg-amber-100 transition-colors"
                >
                  Merchant: <strong>{ocrSuggestions.merchant}</strong>
                </button>
              )}
              <button
                type="button"
                onClick={() => applySuggestions({
                  ...(ocrSuggestions.amount   ? { amount:   ocrSuggestions.amount }   : {}),
                  ...(ocrSuggestions.date     ? { date:     ocrSuggestions.date }     : {}),
                  ...(ocrSuggestions.merchant ? { merchant: ocrSuggestions.merchant } : {}),
                })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 transition-colors"
              >
                Apply all
              </button>
            </div>
          </div>
        )}

        {/* Details form */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Expense details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                Date *
              </label>
              <input
                type="date"
                id="date"
                name="date"
                required
                value={formData.date}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                Amount *
              </label>
              <div className="mt-1 flex flex-col gap-3">
                <div className="flex rounded-md shadow-sm gap-0">
                  <input
                    type="number"
                    id="amount"
                    name="amount"
                    required
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={handleChange}
                    className="min-w-0 flex-1 rounded-l-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className="rounded-r-md border border-l-0 border-gray-300 px-2 sm:px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white w-20 sm:w-32 flex-shrink-0"
                  >
                    {CURRENCIES.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.flag} {currency.code} - {currency.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* EUR Conversion Display - Always visible when currency is not EUR */}
                {formData.currency !== 'EUR' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <div className="text-sm flex-1">
                      {formData.amount && parseFloat(formData.amount) > 0 ? (
                        convertedAmount ? (
                          <>
                            <span className="font-medium text-blue-900">
                              💶 {convertedAmount.toFixed(2)} EUR equivalent
                            </span>
                            <span className="text-blue-700 ml-2 block text-xs mt-1">
                              (1 {formData.currency} = {(conversionRate || 0).toFixed(4)} EUR)
                            </span>
                          </>
                        ) : (
                          <span className="text-blue-900 italic">Calculating conversion...</span>
                        )
                      ) : (
                        <span className="text-blue-900 italic">Enter amount to see EUR conversion</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <AutocompleteInput
                id="merchant"
                name="merchant"
                label="Merchant"
                value={formData.merchant}
                onChange={handleChange}
                suggestions={suggestions.merchants}
                placeholder="Merchant name"
              />
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                Category
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Select...</option>
                {EXPENSE_CATEGORIES.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <AutocompleteInput
                id="client"
                name="client"
                label="Project/Client"
                value={formData.client}
                onChange={handleChange}
                suggestions={suggestions.clients}
                placeholder="Project or client identifier"
              />
            </div>

            <div>
              <label htmlFor="trip_id" className="block text-sm font-medium text-gray-700">
                Trip
              </label>
              <select
                id="trip_id"
                name="trip_id"
                value={formData.trip_id}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">No trip</option>
                {trips.map(trip => (
                  <option key={trip.id} value={String(trip.id)}>
                    {trip.name}{trip.start_date ? ` (${trip.start_date})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <AutocompleteInput
                id="attendees"
                name="attendees"
                label="Attendees (comma-separated)"
                value={formData.attendees}
                onChange={handleChange}
                suggestions={suggestions.attendees}
                placeholder="John Doe, Jane Smith"
                multiValue={true}
              />
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-4 justify-end">
          <button
            type="button"
            onClick={() => navigate('/expenses')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader className="w-5 h-5 mr-2 animate-spin" />
                {isEditing ? 'Updating...' : 'Saving...'}
              </>
            ) : (
              <>
                <Camera className="w-5 h-5 mr-2" />
                {isEditing ? 'Update' : 'Save'}
              </>
            )}
          </button>
        </div>
      </form>

      <PreviewModal
        file={file}
        fileUrl={getPreviewUrl(preview)}
        isPdf={fileType === 'pdf'}
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
      />
    </div>
  );
}

export default ExpenseForm;
